// Central product catalogue — single source of truth
// Prices are in INR paise for backend consistency (₹1,299 = 129900 paise)

const LOGO =
  "https://customer-assets.emergentagent.com/job_0426a88a-d3c7-47bd-847a-d384615aad5e/artifacts/n2do1wid_hl%20logo.jpeg";

const SEA_BUCK_IMG =
  "https://customer-assets.emergentagent.com/job_0426a88a-d3c7-47bd-847a-d384615aad5e/artifacts/thsyvl51_sea%20buckthorn.jpeg";

// Cinematic product hero compositions — only Sea Buckthorn has a real bottle photo.
// For coming-soon products we set heroImage to null so the ProductImage component
// renders a premium composed placeholder (never a mismatched stock photo).
const MAN360_IMG = null;
const SHILAJIT_MEN_IMG = null;
const SHILAJIT_WOMEN_IMG = null;
const SYRUP_IMG = null;

// Gallery mood images (secondary shots per product page). All fall back gracefully via ProductImage.
const HIMALAYA_IMG =
  "https://images.unsplash.com/photo-1519681393784-d120267933ba?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&q=80";
const BERRIES_IMG =
  "https://images.unsplash.com/photo-1565852093870-66948332472a?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&q=80";
const HERBS_MORTAR =
  "https://images.unsplash.com/photo-1492552085122-36706c238263?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&q=80";
const RITUAL_IMG = null;

export const PRODUCTS = [
  {
    slug: "sea-buckthorn-pure-pulp",
    name: "Sea Buckthorn Pure Pulp",
    tagline: "Nature's daily wellness ritual — refined for modern living.",
    shortLine: "Antioxidant-rich Himalayan berry pulp.",
    packSize: "300 ML",
    price: 129900,
    priceDisplay: "₹1,299",
    offer: "BUY 1 GET 1 FREE",
    offerDescription: "Order 1 bottle, receive 2 — a limited-launch pairing.",
    status: "available",
    statusLabel: "In Stock",
    heroImage: SEA_BUCK_IMG,
    gallery: [SEA_BUCK_IMG, BERRIES_IMG, HIMALAYA_IMG, RITUAL_IMG],
    overview:
      "Cold-processed from Himalayan Sea Buckthorn berries — amongst the most nutrient-dense botanicals in the world. Naturally rich in Vitamin C, carotenoids, omega fatty acids and polyphenols. HerbKranti's flagship product, and the quiet beginning of your everyday wellness ritual.",
    philosophy:
      "Sea Buckthorn thrives at Himalayan altitudes where few plants dare grow. Our sourcing partners hand-harvest berries at their nutritional peak, and we cold-process them to preserve the delicate compounds nature spent months creating. Nothing added, nothing implied — only the fruit, gently prepared.",
    benefits: [
      {
        title: "Antioxidant-rich nutrition",
        body: "Sea Buckthorn is amongst nature's most concentrated sources of naturally occurring antioxidants including Vitamin C, E and carotenoids.",
      },
      {
        title: "Skin radiance from within",
        body: "A blend of naturally occurring omega-3, 6, 7 and 9 fatty acids traditionally associated with a soft, everyday glow.",
      },
      {
        title: "Immune support",
        body: "Naturally occurring flavonoids and Vitamin C — botanicals traditionally regarded as supporting the body's natural defenses.",
      },
      {
        title: "Everyday vitality",
        body: "A gentle, considered addition to your morning ritual — the kind of consistent energy that lets you show up, day after day.",
      },
      {
        title: "Digestive wellness",
        body: "Naturally occurring dietary fibre and plant compounds designed to sit gently with the body.",
      },
      {
        title: "Himalayan-sourced purity",
        body: "Hand-harvested berries at their peak, cold-processed to preserve the compounds nature created.",
      },
    ],
    ingredients: [
      { name: "Sea Buckthorn Pulp", latin: "Hippophae rhamnoides", note: "Himalayan berry, our sole hero botanical." },
    ],
    ritualTitle: "The HerbKranti Morning Ritual",
    ritualEyebrow: "How to Use",
    howToUse: [
      { step: "1", label: "Measure", body: "Take 10 ml of HerbKranti Sea Buckthorn Pure Pulp." },
      { step: "2", label: "Mix with water", body: "Add approximately 100 ml of normal drinking water." },
      { step: "3", label: "Stir well", body: "Stir thoroughly before drinking." },
      { step: "4", label: "Morning ritual", body: "Consume every morning on an empty stomach." },
      { step: "5", label: "Then breakfast", body: "Wait at least 20–30 minutes before consuming breakfast for the best daily wellness routine." },
    ],
    wellnessNote: {
      title: "Consistency Matters",
      body:
        "According to Ayurvedic philosophy, meaningful wellness develops through regular daily habits, balanced nutrition, adequate rest and consistent use of quality botanicals. Sea Buckthorn Pure Pulp is intended to become part of a disciplined morning wellness routine.",
    },
    packDetails: {
      bottleSize: "300 ML premium glass bottle",
      shelfLife: "Best consumed within 12 months of manufacture",
      form: "Cold-processed pure pulp",
      certifications: "No artificial additives · No preservatives · No added sugar",
    },
    storage: "Store in a cool, dry place, away from direct sunlight. Refrigerate after opening and consume within 30 days.",
    faqs: [
      { q: "Is Sea Buckthorn safe for daily use?", a: "It is a food, not a medicine — a considered daily ritual for adults. If you are pregnant, nursing or on prescribed medication, consult your physician." },
      { q: "What does it taste like?", a: "Tart, tangy and unmistakably fruity — think a wilder, more concentrated citrus. Best enjoyed diluted." },
      { q: "Is it vegan?", a: "Yes — 100% plant-based." },
      { q: "How is the BUY 1 GET 1 FREE offer honoured?", a: "Order one 300 ML bottle at ₹1,299 and we ship two bottles. The offer is automatically applied at checkout." },
    ],
  },
  {
    slug: "man-360",
    name: "MAN 360",
    tagline: "A considered daily rasayana. Crafted for modern men.",
    shortLine: "Premium Ayurvedic herbal granulated powder — 13 carefully selected botanicals.",
    packSize: "150 g",
    price: null,
    priceDisplay: null,
    offer: null,
    offerDescription: null,
    status: "coming-soon",
    statusLabel: "Coming Soon",
    heroImage: MAN360_IMG,
    gallery: [MAN360_IMG, HERBS_MORTAR, HIMALAYA_IMG, RITUAL_IMG],
    overview:
      "MAN 360 is a premium Ayurvedic herbal granulated formulation inspired by the classical rasayana tradition. Thirteen carefully selected botanicals — including Shilajit, Ashwagandha, Safed Musli, Shatavari, Gokshura and Kauch Beej — sourced from India's most trusted botanical partners and prepared as premium free-flowing herbal granules, thoughtfully designed for a modern man's daily rhythm.",
    philosophy:
      "Rasayana in Sanskrit means 'the path of essence.' It's not a supplement in the modern sense — it's a considered category of botanicals used in Ayurveda to support the whole body, day after day. MAN 360 draws from this tradition, adapting it for the pace, the demands and the aesthetic of modern life.",
    benefits: [
      { title: "Considered daily composition", body: "Thirteen botanicals selected across the classical rasayana tradition, thoughtfully blended into a premium herbal granulated formulation." },
      { title: "Sustained vitality", body: "Traditional Ayurvedic herbs long associated with steady, day-long energy — not stimulant-driven." },
      { title: "Strength & resilience", body: "Adaptogenic botanicals like Ashwagandha and Safed Musli, revered for their supportive daily role." },
      { title: "Whole-body support", body: "MAN 360 is designed as a broad-spectrum daily wellness ritual, not a single-issue product." },
      { title: "Premium granulated format", body: "Free-flowing herbal granules, thoughtfully packaged in a food-grade airtight jar to preserve freshness, aroma and quality." },
      { title: "Provenance you can trust", body: "Every ingredient individually sourced and researched from India's most trusted botanical partners." },
    ],
    ingredientKey: "man360-13",
    ritualTitle: "The HerbKranti Ritual",
    ritualEyebrow: "How to Use",
    howToUse: [
      { step: "1", label: "Warm the milk", body: "Warm approximately 150 ml of milk." },
      { step: "2", label: "Add desi ghee", body: "Add 2 teaspoons of pure desi ghee." },
      { step: "3", label: "Add MAN 360", body: "Add 5 grams of MAN 360 Premium Herbal Granules." },
      { step: "4", label: "Mix thoroughly", body: "Stir until the granules are evenly blended into the milk." },
      { step: "5", label: "Bedtime ritual", body: "Consume at night before going to bed." },
      { step: "6", label: "The last of the day", body: "For best results, this should ideally be the last meal or nutritional intake of the day." },
    ],
    wellnessNote: {
      title: "Consistency Creates the Difference",
      body:
        "According to the Ayurvedic philosophy, wellness develops through consistency rather than shortcuts. MAN 360 is designed to become part of a disciplined daily routine — alongside balanced nutrition, regular exercise, quality sleep and a healthy lifestyle.",
    },
    packDetails: {
      productForm: "Premium Herbal Granulated Powder",
      netWeight: "150 g",
      servingSize: "As recommended on the official product label after launch.",
      packaging: "Premium food-grade airtight jar designed to preserve freshness, aroma and product quality.",
      texture: "Premium free-flowing herbal granules.",
      howToConsume: "The final usage instructions will be published with the official product launch.",
      certifications: "The final manufacturing details and certifications will be published with the official launch.",
    },
    storage:
      "Store in a cool and dry place away from direct sunlight. Keep the container tightly closed after every use. Keep away from moisture.",
    faqs: [
      { q: "When does MAN 360 launch?", a: "The formulation is currently in development. Join the waitlist to be notified on launch day." },
      { q: "What format does MAN 360 come in?", a: "MAN 360 is a premium Ayurvedic herbal granulated powder — a free-flowing herbal formulation consumed with warm milk and desi ghee, as detailed in the HerbKranti Ritual." },
      { q: "Will it have the six hero ingredients you've highlighted?", a: "The current botanical direction includes Shilajit, Ashwagandha, Safed Musli, Shatavari, Gokshura and Kauch Beej as leading ingredients. The final blend and proportions will be published on launch." },
      { q: "Are all 13 ingredients confirmed?", a: "The 13-ingredient direction reflects the current formulation intent and may be refined before official launch." },
      { q: "Is MAN 360 for sale right now?", a: "No. This is a pre-launch listing for educational and waitlist purposes only." },
    ],
  },
  {
    slug: "shilajit-for-men",
    name: "Premium Shilajit for Men",
    tagline: "Authentic Himalayan resin. Considered for daily men's wellness.",
    shortLine: "Purified Himalayan mineral resin, thoughtfully sourced.",
    packSize: "20 GM",
    price: null,
    priceDisplay: null,
    offer: null,
    status: "coming-soon",
    statusLabel: "Coming Soon",
    heroImage: SHILAJIT_MEN_IMG,
    gallery: [SHILAJIT_MEN_IMG, HIMALAYA_IMG, HERBS_MORTAR, RITUAL_IMG],
    overview:
      "A premium purified Shilajit resin sourced from high-altitude Himalayan formations. Traditionally regarded in Ayurveda as a rasayana — a preparation associated with vitality, resilience and everyday wellness in men.",
    philosophy:
      "Shilajit is not manufactured — it is uncovered. Formed over centuries in mountain crevices, our resin is triple-purified and lab-tested to remove impurities while preserving its natural mineral profile. Considered sourcing, transparent testing.",
    benefits: [
      { title: "Traditional men's rasayana", body: "Classically referenced in Ayurveda as a daily botanical for men's holistic wellness." },
      { title: "Natural mineral profile", body: "Contains naturally occurring fulvic acid, humic acid and trace minerals." },
      { title: "Sustained daily support", body: "Traditionally used as a considered daily ritual — not a one-time boost." },
      { title: "Provenance-first", body: "Sourced from carefully selected Himalayan formations, triple-purified." },
      { title: "Lab-tested purity", body: "Every batch tested for authenticity, purity and heavy-metal safety." },
      { title: "Considered daily dose", body: "Delivered in a premium resin format — a small daily gesture, ritualised." },
    ],
    ingredients: [
      { name: "Purified Shilajit Resin", latin: "Asphaltum punjabianum", note: "Triple-purified Himalayan mineral resin, our sole ingredient." },
    ],
    howToUse: [
      { step: "1", label: "Portion", body: "A pea-sized portion (approximately 300-500 mg) daily." },
      { step: "2", label: "Dissolve", body: "Stir into warm water, warm milk, or a considered morning beverage." },
      { step: "3", label: "Ritual", body: "Best consumed on an empty stomach in the morning." },
    ],
    packDetails: {
      bottleSize: "20 GM premium amber glass jar",
      shelfLife: "TBA on official launch",
      form: "Purified resin",
      certifications: "Lab-tested batch reports will be published on launch.",
    },
    storage: "Store in a cool, dry place. Resin may soften in warm temperatures — this is natural and safe.",
    faqs: [
      { q: "When does this launch?", a: "Currently in final quality checks. Join the waitlist for launch day." },
      { q: "Is it truly Himalayan?", a: "Yes — sourced from partners who harvest from high-altitude Himalayan formations. Batch reports will be published." },
      { q: "Difference from MAN 360?", a: "This is pure Shilajit. MAN 360 is a broader 13-botanical composition where Shilajit is one of the hero ingredients." },
    ],
  },
  {
    slug: "shilajit-for-women",
    name: "Premium Shilajit for Women",
    tagline: "Authentic Himalayan resin. Considered for daily women's wellness.",
    shortLine: "Purified Himalayan mineral resin, thoughtfully sourced.",
    packSize: "20 GM",
    price: null,
    priceDisplay: null,
    offer: null,
    status: "coming-soon",
    statusLabel: "Coming Soon",
    heroImage: SHILAJIT_WOMEN_IMG,
    gallery: [SHILAJIT_WOMEN_IMG, HIMALAYA_IMG, HERBS_MORTAR, RITUAL_IMG],
    overview:
      "A premium purified Shilajit resin, thoughtfully positioned for women's daily wellness rituals. Traditionally regarded in Ayurveda as a rasayana — supporting vitality, resilience and everyday balance.",
    philosophy:
      "Shilajit has been a considered part of India's classical wellness tradition for both men and women. Our resin is the same high-altitude, triple-purified sourcing — presented thoughtfully as a considered daily ritual for women.",
    benefits: [
      { title: "Traditional daily rasayana", body: "A classical Ayurvedic botanical, considered for daily women's wellness." },
      { title: "Natural mineral profile", body: "Contains naturally occurring fulvic acid, humic acid and trace minerals." },
      { title: "Everyday balance", body: "Traditionally associated with steady, day-to-day vitality — the kind you don't notice, until you do." },
      { title: "Provenance-first", body: "Sourced from high-altitude Himalayan formations, triple-purified." },
      { title: "Lab-tested purity", body: "Every batch tested for authenticity, purity and heavy-metal safety." },
      { title: "Considered daily ritual", body: "A small, elegant morning gesture — beautifully packaged, thoughtfully consumed." },
    ],
    ingredients: [
      { name: "Purified Shilajit Resin", latin: "Asphaltum punjabianum", note: "Triple-purified Himalayan mineral resin." },
    ],
    howToUse: [
      { step: "1", label: "Portion", body: "A pea-sized portion (approximately 300-500 mg) daily." },
      { step: "2", label: "Dissolve", body: "Stir into warm water, warm milk, or a considered morning beverage." },
      { step: "3", label: "Ritual", body: "Best consumed on an empty stomach in the morning." },
    ],
    packDetails: {
      bottleSize: "20 GM premium amber glass jar",
      shelfLife: "TBA on official launch",
      form: "Purified resin",
      certifications: "Lab-tested batch reports will be published on launch.",
    },
    storage: "Store in a cool, dry place. Resin may soften in warm temperatures — this is natural and safe.",
    faqs: [
      { q: "How is this different from Shilajit for Men?", a: "The resin is identically sourced and purified. The presentation and daily ritual guidance are thoughtfully positioned for women." },
      { q: "Is it safe during pregnancy?", a: "If you are pregnant, nursing or on prescribed medication, please consult your physician before use." },
      { q: "When does it launch?", a: "Currently in final quality checks. Join the waitlist to be notified." },
    ],
  },
  {
    slug: "sugar-free-wellness-syrup",
    name: "Sugar-Free Wellness Syrup",
    tagline: "A considered daily syrup. Zero refined sugar. Full botanical intent.",
    shortLine: "Ayurveda-inspired daily syrup, without the sweetness compromise.",
    packSize: "500 ML",
    price: null,
    priceDisplay: null,
    offer: null,
    status: "coming-soon",
    statusLabel: "Coming Soon",
    heroImage: SYRUP_IMG,
    gallery: [SYRUP_IMG, HERBS_MORTAR, HIMALAYA_IMG, RITUAL_IMG],
    overview:
      "A premium daily wellness syrup composed of considered botanicals from the Ayurvedic tradition — with zero refined sugar. A modern reinterpretation of classical Indian tonics, thoughtfully crafted for those who watch what they consume.",
    philosophy:
      "Classical Indian tonics often relied on sugar for palatability. We've reformulated the format — botanicals remain classical, sweetness is sourced from considered natural alternatives. No compromise on tradition, no compromise on modern intent.",
    benefits: [
      { title: "Zero refined sugar", body: "Sweetened only with considered natural alternatives — no cane sugar, no syrups." },
      { title: "Classical Ayurvedic botanicals", body: "Composed of traditional herbs long-referenced in Ayurvedic wellness texts." },
      { title: "Daily wellness ritual", body: "Designed for consistent daily use — a small, considered gesture." },
      { title: "Modern format", body: "Elegant, palatable, and delivered in a considered premium glass bottle." },
      { title: "Considered composition", body: "Every ingredient chosen for its role in the whole — not filler, not padding." },
      { title: "Transparent labelling", body: "Complete ingredient disclosure, no proprietary blend obfuscation." },
    ],
    ingredients: [
      { name: "Blend of Classical Ayurvedic Botanicals", latin: "Multi-herb formulation", note: "Full formulation will be published on official launch." },
    ],
    howToUse: [
      { step: "1", label: "Daily", body: "10-15 ML once or twice daily as directed on the label." },
      { step: "2", label: "Dilute", body: "Take directly or dilute in warm water." },
      { step: "3", label: "Consistent", body: "Best consumed as part of a daily wellness routine." },
    ],
    packDetails: {
      bottleSize: "500 ML premium glass bottle",
      shelfLife: "TBA on official launch",
      form: "Syrup",
      certifications: "Zero refined sugar · Full ingredient disclosure on launch.",
    },
    storage: "Store in a cool, dry place, away from direct sunlight. Refrigerate after opening.",
    faqs: [
      { q: "Is it truly sugar-free?", a: "Yes — no refined cane sugar or corn syrup. Sweetened with considered natural alternatives." },
      { q: "Is it suitable for diabetics?", a: "Please consult your physician before adding any new product to your daily routine." },
      { q: "When does it launch?", a: "In final development. Join the waitlist to be notified on launch day." },
    ],
  },
];

export const findProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
export const AVAILABLE_PRODUCTS = PRODUCTS.filter((p) => p.status === "available");

export const IMAGES = { LOGO, SEA_BUCK_IMG, HIMALAYA_IMG, BERRIES_IMG, HERBS_MORTAR, RITUAL_IMG };
